import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import {
  Button,
  Checkbox,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Tooltip,
} from '@mui/material';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';

import { IUnitListItem } from 'src/types/unit';
import {
  IKitchenTableFilters,
  IKitchenTableFilterValue,
  KITCHEN_ALERT_OPTIONS,
} from 'src/types/kitchen';

type Props = {
  filters: IKitchenTableFilters;
  onFilters: (name: string, value: IKitchenTableFilterValue) => void;
  units: IUnitListItem[];
  handleResetFilters: VoidFunction;
  canReset: boolean;
};

const ALERT_STATUS_OPTIONS = [{ value: 'all', label: 'All' }, ...KITCHEN_ALERT_OPTIONS];

export default function KitchenTableToolbar({
  filters,
  onFilters,
  units,
  handleResetFilters,
  canReset,
}: Props) {
  const [localMinQuantity, setLocalMinQuantity] = useState('');
  const [localMaxQuantity, setLocalMaxQuantity] = useState('');
  const [input, setInput] = useState<string>('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value) => {
      onFilters('generalSearch', value);
    }, 500),
    []
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedMinQuantity = useCallback(
    debounce((value) => {
      onFilters('minQuantity', value);
    }, 500),
    []
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedMaxQuantity = useCallback(
    debounce((value) => {
      onFilters('maxQuantity', value);
    }, 500),
    []
  );

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
      debouncedMinQuantity.cancel();
      debouncedMaxQuantity.cancel();
    };
  }, [debouncedSearch, debouncedMinQuantity, debouncedMaxQuantity]);

  const handleFilterUnits = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      onFilters('units', event.target.value);
    },
    [onFilters]
  );

  const handleFilterAlerts = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      onFilters('alertsEnabled', event.target.value);
    },
    [onFilters]
  );

  const handleMinQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setLocalMinQuantity(value);

    debouncedMinQuantity(value === '' ? null : value);
  };

  const handleMaxQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setLocalMaxQuantity(value);

    debouncedMaxQuantity(value === '' ? null : value);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setInput(value);

    debouncedSearch(value);
  };

  const clearSearchText = () => {
    setInput('');

    debouncedSearch('');
  };

  const clearAll = useCallback(() => {
    setInput('');
    setLocalMinQuantity('');
    setLocalMaxQuantity('');
    handleResetFilters();
  }, [handleResetFilters]);

  const hasQuantityError =
    Number(localMaxQuantity) < Number(localMinQuantity) && localMaxQuantity !== '';
  const hasNegativeMinQuantity = Number(localMinQuantity) < 0;
  const hasNegativeMaxQuantity = Number(localMaxQuantity) < 0;

  return (
    <Stack
      spacing={2}
      alignItems={{ xs: 'flex-end', md: 'center' }}
      direction={{
        xs: 'column',
        md: 'row',
      }}
      sx={{
        p: 2.5,
        pr: { xs: 2.5, md: 1 },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2} flexGrow={1} sx={{ width: 1 }}>
        <TextField
          fullWidth
          value={input}
          onChange={handleSearchChange}
          placeholder="Search for item name, item code, barcode..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
            endAdornment:
              input.length > 0 ? (
                <InputAdornment position="end">
                  <IconButton onClick={clearSearchText}>
                    <Iconify icon="eva:close-fill" color="text.disabled" />
                  </IconButton>
                </InputAdornment>
              ) : null,
          }}
          inputProps={{
            autoComplete: 'off',
          }}
        />
      </Stack>

      <Stack direction="row" alignItems="center" spacing={2}>
        <TextField
          label="Min Quantity"
          value={localMinQuantity}
          onChange={handleMinQuantityChange}
          type="number"
          placeholder="Min Quantity"
          sx={{
            minWidth: 150,
          }}
          inputProps={{
            min: 0,
            autoComplete: 'off',
          }}
          onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
          error={hasNegativeMinQuantity}
          InputProps={{
            endAdornment: hasNegativeMinQuantity ? (
              <InputAdornment position="end">
                <Tooltip title="Min quantity cannot be negative">
                  <Iconify icon="eva:info-outline" color="error.main" />
                </Tooltip>
              </InputAdornment>
            ) : null,
          }}
        />
        <TextField
          label="Max Quantity"
          value={localMaxQuantity}
          onChange={handleMaxQuantityChange}
          type="number"
          placeholder="Max Quantity"
          sx={{
            minWidth: 150,
          }}
          inputProps={{
            min: 0,
            autoComplete: 'off',
          }}
          error={hasQuantityError || hasNegativeMaxQuantity}
          InputProps={{
            endAdornment:
              hasQuantityError || hasNegativeMaxQuantity ? (
                <InputAdornment position="end">
                  <Tooltip
                    title={
                      hasNegativeMaxQuantity
                        ? 'Max Quantity cannot be negative'
                        : 'Max Quantity should be greater than or equal to Min Quantity'
                    }
                  >
                    <Iconify icon="eva:info-outline" color="error.main" />
                  </Tooltip>
                </InputAdornment>
              ) : null,
          }}
          onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
        />
      </Stack>

      <FormControl
        sx={{
          flexShrink: 0,
          width: { xs: 1, md: 180 },
        }}
      >
        <InputLabel>Units</InputLabel>

        <Select
          multiple
          value={filters.units}
          onChange={handleFilterUnits}
          input={<OutlinedInput label="Units" />}
          renderValue={(selected) => (
            <Label variant="soft" color="primary">
              Units {selected.length < 10 ? `0${selected.length}` : selected.length}
            </Label>
          )}
          sx={{ textTransform: 'capitalize' }}
        >
          {units.map((option) => (
            <MenuItem key={option._id} value={option._id}>
              <Checkbox disableRipple size="small" checked={filters.units.includes(option._id)} />
              {option.abbreviation}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl
        sx={{
          flexShrink: 0,
          width: { xs: 1, md: 180 },
        }}
      >
        <InputLabel>Alerts</InputLabel>

        <Select
          multiple={false}
          value={[filters.alertsEnabled]}
          onChange={handleFilterAlerts}
          input={<OutlinedInput label="Alerts" />}
          renderValue={(selected) => (
            <Label variant="soft" color="primary">
              {selected}
            </Label>
          )}
          sx={{ textTransform: 'capitalize' }}
        >
          {ALERT_STATUS_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {canReset && (
        <Button
          color="error"
          onClick={clearAll}
          variant="text"
          sx={{ textDecoration: 'underline' }}
        >
          Clear
        </Button>
      )}
    </Stack>
  );
}
